import React, { useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { useHistory } from 'react-router-dom';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import { useLanguageLocale } from '../../hooks/language-locale-hooks';
import { GUID_REGEX, RESTRICT_DEVICE_WITHOUT_SPACE_REGEX } from '../../constants';
import { useDeviceMaster } from '../../data/DataProviders/DeviceMasterProvider';
import { useCurrentAppRoute } from '../../hooks/app-route-hooks';
import { pushAppRoute } from '../../data/domain/route-manager';
import { isDataLoaded } from '../../types';
import CircularProgress from '@mui/material/CircularProgress';
import deviceStyles from '../../app/device-management/DeviceStyle';
import { ConfirmationPopUp } from '../../components/ConfirmationPopUp/ConfirmationPopUp';
import { PxdbDeviceRegistration } from '../../types/device';
import { NavigationPromptModal } from '../NavigationPromptModal/NavigationPromptModal';
import { useDispatch, useSelector } from 'react-redux';
import { useToasts } from '../../data/DataProviders/ToastProvider';
import { useAuthHelper } from '../AuthContextProvider';
import { Thunks } from '../../actions';
import { PxdbDeviceSelectors } from '../../selectors/pxdb-device-selectors';
import { PxdbDeviceActions } from '../../actions/actions/pxdb-device-actions';

export const AddPXDBDevice: React.FC<{
    onCancel: Function;
    siteId: string;
    organizationName: string;
    selectedDevice: string;
    deviceTypeList: Array<{ key: string; value: string; gatewayProfileId: string; netmask: number }>;
    device: string;
}> = (props) => {
    const history = useHistory();
    const { t } = useLanguageLocale();
    const classes = deviceStyles();
    const deviceMasterData = useDeviceMaster();
    const dispatch = useDispatch();
    const { addToast } = useToasts();
    const authHelper = useAuthHelper();
    const currentAppRoute = useCurrentAppRoute();
    const pxdbDeviceState = useSelector(PxdbDeviceSelectors.deviceRegistration);
    const [pxdbgatewayDevice, setPxdbGatewayDevice] = useState<PxdbDeviceRegistration>();
    const [guidAddressError, setGUIDAddressError] = React.useState(false);

    const onChangeValues = (value: string | number, type: string): void => {
        if (pxdbgatewayDevice) {
            const deviceObj: any = { ...pxdbgatewayDevice };
            deviceObj[`${type}`] = value;
            setPxdbGatewayDevice(deviceObj);
        }
    };

    const onChangeName = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
        if (RESTRICT_DEVICE_WITHOUT_SPACE_REGEX.test(e.target.value)) onChangeValues(e.target.value, 'name');
    };

    const onChangeGuid = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
        onChangeValues(e.target.value, 'deviceId');
    };

    const onDevicesCancel = (): void => {
        props.onCancel();
    };

    const navigateToDeviceProfile = (): void => {
        dispatch(PxdbDeviceActions.pxdbGatewayDeviceRegistrationUnmounted());
        if (currentAppRoute.type === 'AddDevice' && pxdbgatewayDevice)
            pushAppRoute(history, {
                type: 'PxDeviceProfile',
                orgId: currentAppRoute.orgId,
                locationId: currentAppRoute.locationId,
                siteId: currentAppRoute.siteId,
                deviceId: pxdbgatewayDevice.deviceId,
            });
    };

    const handleClose = (): void => {
        navigateToDeviceProfile();
    };

    const isDirty =
        (pxdbgatewayDevice && (pxdbgatewayDevice.name !== '' || pxdbgatewayDevice.deviceId !== '')) || false;

    const isDisablePxdb = pxdbgatewayDevice && pxdbgatewayDevice.name.trim() && pxdbgatewayDevice.deviceId !== ''; //GUID

    const isSubmitDisable = (): boolean | undefined => {
        if (!pxdbDeviceState.pxdbgatewayLoading && Boolean(isDisablePxdb)) return true;
    };

    const copiedConnectionString = (): void => {
        if (pxdbDeviceState.pxdbgatewaySuccess) {
            navigator.clipboard.writeText(pxdbDeviceState.connectionString);
        }
    };

    useEffect(() => {
        dispatch(PxdbDeviceActions.pxdbGatewayDeviceMasterData(true));
        if (props.siteId !== '' && isDataLoaded(deviceMasterData.deviceTypeData)) {
            const gatewayObj = props.deviceTypeList.find(
                (x) => x.key.toLowerCase() === props.selectedDevice.toLowerCase()
            );

            const device: PxdbDeviceRegistration = {
                siteId: props.siteId,
                name: '',
                deviceId: '', //GUID
                serial: 'PXDB',
                deviceProfileID: gatewayObj?.gatewayProfileId,
            };
            setPxdbGatewayDevice(device);
        }
    }, [props.siteId, deviceMasterData.deviceTypeData, props.selectedDevice]); //eslint-disable-line react-hooks/exhaustive-deps

    const onSubmit = (): void => {
        //console.log('pxdb registration');
        let checkGUIDAddress = true;
        //console.log('pxdbgatewayDevice registration', pxdbgatewayDevice);
        if (pxdbgatewayDevice && pxdbgatewayDevice.deviceProfileID) {
            // console.log('pxdbgatewayDevice registration', pxdbgatewayDevice);
            if (!GUID_REGEX.test(pxdbgatewayDevice?.deviceId)) {
                setGUIDAddressError(true);
                checkGUIDAddress = false;
            } else {
                setGUIDAddressError(false);
                checkGUIDAddress = true;
            }
            if (checkGUIDAddress) {
                const pxgatewayDeviceData = {
                    name: pxdbgatewayDevice.name,
                    serial: 'PXDB',
                    deviceId: pxdbgatewayDevice.deviceId,
                    siteId: props.siteId,
                    deviceProfileID: pxdbgatewayDevice.deviceProfileID,
                };

                dispatch(Thunks.PXDBDevice.PxdbDeviceRegistration(pxgatewayDeviceData, authHelper, addToast));
            }
        }
    };

    return (
        <Box>
            {pxdbgatewayDevice && (
                <>
                    <Box className={classes.padding8}>
                        <TextField
                            inputProps={{ 'data-testid': 'device-name' }}
                            className={classes.fullWidth}
                            required
                            variant="filled"
                            value={pxdbgatewayDevice.name}
                            id="DeviceName"
                            label="Connected Device Name"
                            onChange={(e): void => onChangeName(e)}
                        />
                    </Box>

                    <Box className={classes.padding8}>
                        <TextField
                            inputProps={{ 'data-testid': 'device-guid' }}
                            className={classes.fullWidth}
                            required
                            variant="filled"
                            value={pxdbgatewayDevice.deviceId}
                            id="DeviceGUID"
                            label="GUID"
                            onChange={(e): void => onChangeGuid(e)}
                            error={guidAddressError}
                            helperText={guidAddressError ? 'Invalid GUID' : ''}
                        />
                    </Box>

                    <Box style={{ display: 'flex', justifyContent: 'flex-end', padding: '24px 8px' }}>
                        <Button
                            style={{ marginRight: 16 }}
                            variant="outlined"
                            color="primary"
                            data-testid="cancel-device"
                            onClick={(): void => onDevicesCancel()}
                        >
                            {t('ACTIONS.CANCEL')}
                        </Button>
                        <Button
                            variant="contained"
                            disabled={!isSubmitDisable()}
                            startIcon={
                                pxdbDeviceState.pxdbgatewayLoading ? (
                                    <CircularProgress size="1rem" style={{ color: 'white' }} />
                                ) : (
                                    <></>
                                )
                            }
                            color="primary"
                            data-testid="add-device-button"
                            onClick={onSubmit}
                        >
                            {t('ACTIONS.SUBMIT')}
                        </Button>
                    </Box>
                </>
            )}

            {pxdbDeviceState.pxdbgatewaySuccess && (
                <ConfirmationPopUp
                    title="Submission Successful!"
                    body={
                        <div style={{ wordWrap: 'break-word' }}>
                            <Typography variant="body2">Your Connected Device is being remotely configured.</Typography>
                            <Typography variant="body2" style={{ marginBottom: 10 }}>
                                Please allow 10 to 90 minutes for this configuration to be downloaded to your Gateway.
                            </Typography>
                            <Typography variant="subtitle2">
                                PX Connection String. Use the button below to copy this connection string for use in the
                                PX Dashboard.
                            </Typography>
                            <Typography variant="body2">{pxdbDeviceState.connectionString}</Typography>
                        </div>
                    }
                    handleCopy={copiedConnectionString}
                    copyClipboardButtonText={'Copy PX String To Clipboard'}
                    okButtonText={'OK'}
                    show={pxdbDeviceState.pxdbgatewaySuccess}
                    handleOk={handleClose}
                    okButtonShow={true}
                    copyClipboardButtonShow={true}
                    disableOk={true}
                    width="xs"
                ></ConfirmationPopUp>
            )}
            {!pxdbDeviceState.pxdbgatewaySuccess && <NavigationPromptModal isDirty={isDirty} />}
        </Box>
    );
};
